import * as React from 'react';
import { Col, Row } from 'react-bootstrap';
import type { WrappedFieldProps } from 'redux-form';
import { Field } from 'redux-form';

import type FieldWorkClassificationCodeListItemVM from 'ab-viewModels/fieldWorkClassificationCode/listItem';
import type { EquipmentListItemVM } from 'ab-viewModels/timeSplitEquipment/timeSplitEquipment';
import type { SubjobVM } from 'ab-viewModels/workRequest/jobUpsert.viewModel';

import * as TimeUtils from '@acceligentllc/shared/utils/time';

import { ResourceStatus } from '@acceligentllc/shared/enums/resourceStatus';

import { bemElement } from 'af-utils/bem.util';

import { Button } from '@acceligentllc/storybook';

import Dropdown from 'af-fields/Dropdown';
import DurationPicker, { TimeRoundingInterval } from 'af-fields/DurationPicker';

import LockedValue from 'af-components/LockedValue';

import EquipmentItem from './EquipmentItem';
import type { TimeSplitEntryFormModel } from '../formModel';
import { TimeSplitEntryAction } from '../TimeSheetEditModalBody';

type TimeSplitEntryEquipmentSections = {
	title: string;
	options: EquipmentListItemVM[];
};

interface OwnProps {
	index: number;
	canEditClassificationCodes: boolean;
	canSeeTimeAllocations: boolean;
	classificationCodes: FieldWorkClassificationCodeListItemVM[];
	readonly: boolean;
	sections: Nullable<TimeSplitEntryEquipmentSections>[];
	classificationCodeIdsWithValues: Record<number, string>;
	equipmentIdsWithValues: Record<number, string>;
	subJobs: SubjobVM[];
	entry: TimeSplitEntryFormModel;
	change: (fieldName: string, value) => void;
	getEntry: (index: number) => TimeSplitEntryFormModel;
	removeEntry: (index: number) => void;
	changeEntries: (action: TimeSplitEntryAction, entryIndex: Nullable<number>) => void;
	shouldRoundTimeIntervalsTo1Min: boolean;
	canSeeClassificationCode: boolean;
}

const FILTER_CLASSIFICATION_CODE_BY = ['code'];
const FILTER_EQUIPMENT_BY: (keyof EquipmentListItemVM)[] = ['code', 'specification'];

type Props = OwnProps & WrappedFieldProps;

const TimeSplitEntry: React.FC<Props> = (props: Props) => {

	const {
		index,
		canEditClassificationCodes,
		classificationCodes,
		sections,
		removeEntry,
		readonly,
		input,
		entry,
		change,
		changeEntries,
		classificationCodeIdsWithValues,
		equipmentIdsWithValues,
		subJobs,
		canSeeTimeAllocations,
		shouldRoundTimeIntervalsTo1Min,
		canSeeClassificationCode,
	} = props;

	const classificationCodeName = React.useMemo(() => `${input.name}.fieldWorkClassificationCodeId`, [input.name]);
	const equipmentName = React.useMemo(() => `${input.name}.equipmentId`, [input.name]);
	const durationName = React.useMemo(() => `${input.name}.duration`, [input.name]);
	const allocatedWorkRequestName = React.useMemo(() => `${input.name}.allocatedWorkRequestId`, [input.name]);

	const timeValue = React.useMemo(() => TimeUtils.minutesToHoursAndMinutes(entry.duration), [entry.duration]);

	const onFieldChange = React.useCallback((fieldName: string) => {
		return (value) => {
			if (fieldName === equipmentName) {
				value = value.id;
			}
			if (fieldName === durationName) {
				value = Number(value);
			}
			change(fieldName, value);
			changeEntries(TimeSplitEntryAction.FIELD_CHANGED, index);
		};
	}, [change, changeEntries, durationName, equipmentName, index]);

	const onDeleteEntry = React.useCallback(() => {
		removeEntry(index);
	}, [removeEntry, index]);

	const selectedClassificationCodeValue = React.useMemo(() => entry.fieldWorkClassificationCodeId ?
		classificationCodeIdsWithValues[entry.fieldWorkClassificationCodeId]
		: undefined, [classificationCodeIdsWithValues, entry.fieldWorkClassificationCodeId]);
	const selectedEquipmentValue = React.useMemo(() => entry.equipmentId ?
		equipmentIdsWithValues[entry.equipmentId]
		: undefined, [entry.equipmentId, equipmentIdsWithValues]);
	const allocatedJobCode = React.useMemo(
		() => subJobs.find((_e) => _e.id === entry.allocatedWorkRequestId)?.jobCode,
		[entry.allocatedWorkRequestId, subJobs]
	);

	const sectionOptions = React.useMemo(() => sections?.map((section) => ({
		title: section?.title,
		options: section?.options.filter((option) => option?.status !== ResourceStatus.DELETED),
	})), [sections]);

	const renderEquipmentItem = React.useCallback((item: Nullable<EquipmentListItemVM> | number) => { // It's number (id) if selected equipment is deleted
		const itemToShow = typeof item === 'number' ?
			sections?.[0]?.options?.find((option) => option?.id === item) ?? sections[1]?.options?.find((option) => option?.id === item)
			: item;

		if (!itemToShow) {
			return null;
		}

		return (
			EquipmentItem(itemToShow)
		);
	}, [sections]);

	if (readonly) {

		return (
			<div className={bemElement('time-sheet-list__equipment-modal__time-split-list', 'row')} key={`time-split-${entry.virtualId}`}>
				<Row>
					{
						canSeeClassificationCode &&
						<Col md={6}><LockedValue label="Classification Code" value={selectedClassificationCodeValue} /></Col>
					}
					<Col md={canEditClassificationCodes ? (canSeeTimeAllocations ? 6 : 8) : 10}><LockedValue label="Equipment" value={selectedEquipmentValue} /></Col>

					<Col md={canEditClassificationCodes ? (canSeeTimeAllocations ? 6 : 8) : 6}><LockedValue label="Time" value={timeValue} /></Col>

					{
						canSeeTimeAllocations &&
						<Col md={6}><LockedValue label="Allocated to sub-job" value={allocatedJobCode} /></Col>
					}
				</Row>
			</div>
		);
	}

	return (
		<div className={bemElement('time-sheet-list__equipment-modal__time-split-list', 'row')} key={`time-split-${entry.virtualId}`}>
			<Row>
				{canEditClassificationCodes && canSeeClassificationCode &&
					<Col className={bemElement('time-sheet-list__equipment-modal__time-split-list__row', 'classification-code')} sm={5}>
						<Field
							component={Dropdown}
							filterable
							filterBy={FILTER_CLASSIFICATION_CODE_BY}
							hasBlankOption
							id={classificationCodeName}
							isFixed={true}
							label="Classification Code"
							labelKey="code"
							name={classificationCodeName}
							onChange={onFieldChange(classificationCodeName)}
							options={classificationCodes}
							value={entry.fieldWorkClassificationCodeId}
							valueKey="id"
							withCaret={true}
						/>
					</Col>
				}
				{canEditClassificationCodes &&
					<>
						<Col sm={canEditClassificationCodes && canSeeTimeAllocations ? 7 : 9}>
							<Field
								component={Dropdown}
								containerClassName={bemElement('time-sheet-list__equipment-modal', 'equipment')}
								filterable={true}
								filterBy={FILTER_EQUIPMENT_BY}
								id={equipmentName}
								label="Equipment"
								name={equipmentName}
								onValueChange={onFieldChange(equipmentName)}
								renderMenuItem={renderEquipmentItem}
								sectionOptionsKey="options"
								sections={sectionOptions}
								sectionTitleKey="title"
								selected={entry.equipmentId}
								useSectionList
								value={entry.equipmentId}
								valueKey="id"
								withCaret={true}
							/>
						</Col>
						<Col sm={canEditClassificationCodes && canSeeTimeAllocations ? 4 : 6}>
							<Field
								component={DurationPicker}
								label="Time"
								labelKey="label"
								name={durationName}
								onValueChange={onFieldChange(durationName)}
								timeRoundingInterval={shouldRoundTimeIntervalsTo1Min
									? TimeRoundingInterval.ONE_MINUTE_INTERVAL
									: TimeRoundingInterval.FIFTEEN_MINUTE_INTERVAL}
								withCaret={true}
							/>
						</Col>
					</>
				}
				{
					canSeeTimeAllocations &&
					<Col className={bemElement('time-sheet-list__equipment-modal__time-split-list__row', 'allocation')} sm={7}>
						<Field
							component={Dropdown}
							filterable={true}
							filterBy={['jobCode']}
							id={allocatedWorkRequestName}
							label={'Allocate to Sub-job'}
							labelKey="jobCode"
							name={allocatedWorkRequestName}
							onChange={onFieldChange(allocatedWorkRequestName)}
							options={subJobs}
							value={entry.allocatedWorkRequestId}
							valueKey="id"
							withCaret={true}
						/>
					</Col>
				}
				<Col sm={1}>
					<div className={bemElement('time-sheet-list__equipment-modal__time-split-list__row', 'delete-button')}>
						<Button
							icon="Delete"
							onClick={onDeleteEntry}
							style="link-danger"
							tooltip="Delete"
						/>
					</div>
				</Col>
			</Row>

		</div>
	);
};

export default TimeSplitEntry;
